import React, { SetStateAction, useState } from "react";
import styles from "../AddMaterialDrawer.module.scss";
import { DraggerConstants, FileUploadConstants } from "./constants";
import UploadIcon from "../../../../../assets/images/upload-icon.svg";
import JpegIcon from "../../../../../assets/images/jpeg-image-icon.svg";
import { Storage } from "aws-amplify";
import { Upload, message, Modal } from "antd";
import type { UploadProps } from "antd";
import Label from "../../../../../reusable-components/Label";
import { FileTypes } from "../../../../../enums/FileUploadEnums";

export interface AddPhotoDraggerProps {
  onFilesUploaded?: () => {};
  docType?: string;
  filesUploaded?: [];
  maxFileCount?: string;
  allowedFileTypes?: string;
  quarryId?: string;
  materialId?: string;
  setDocumentKey: () => {};
  setDocumentMeta: () => void;
  clearReplaceTktError?: () => {};
  disabled: boolean;
  setFileAdded: React.Dispatch<SetStateAction<boolean>>;
}

const AddPhotoDragger = (props: AddPhotoDraggerProps) => {
  const {
    onFilesUploaded,
    docType,
    filesUploaded,
    maxFileCount,
    allowedFileTypes,
    quarryId,
    materialId,
    setDocumentKey,
    setDocumentMeta,
    clearReplaceTktError,
    disabled,
    setFileAdded,
  } = props;
  const { Dragger } = Upload;
  const {
    UPLOAD_PRIMARY_TXT,
    UPLOAD_HINT,
    MAX_FILE_COUNT,
    DEFAULT_SHEET_STATE,
    FILE_UPLOAD_SUCCESS_TXT,
    DRAGGER_LABEL,
    UPLOAD_ICON_ALT,
  } = DraggerConstants;
  const {
    DUPLICATE_WARNING,
    UPLOAD_SUCCESS,
    UPLOAD_FAILED,
    DUPLICATE_TITLE,
    FILE_SIZE_WARNING,
    FILE_TYPE_WARNING_2,
    FILE_TYPE_WARNING,
    MAX_COUNT_WARNING,
    PROGRESS_COLOR,
  } = FileUploadConstants;

  const [fileList, setFileList] = useState(
    filesUploaded?.length ? filesUploaded : []
  );

  const handleUpload = async (file, onSuccess, onError, onProgress) => {
    try {
      const timestampMs = new Date().getTime();
      const result = await Storage.put(
        `Quarries/${quarryId}/${materialId}/${timestampMs}/${file.name}`,
        file,
        {
          contentType: file.type,
          progressCallback: (progress) => {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            onProgress({ percent });
          },
        }
      );
      const fileURL = await Storage.get(result.key, { level: "public" });
      setDocumentKey(result?.key);
      const newFile = {
        name: file.name,
        docAttachment: fileURL?.split("?")[0],
        docAttachmentId: file.uid,
        fileType: file.type,
        sheetStatus: DEFAULT_SHEET_STATE,
        docType: docType,
        document: docType,
        signatureVerified: false,
      };
      setDocumentMeta({
        fileType: file.type,
        fileName: file.name,
      });
      setFileAdded?.(true);
      message.success(UPLOAD_SUCCESS(file.name));
      onSuccess(FILE_UPLOAD_SUCCESS_TXT);
      onFilesUploaded?.(newFile);
    } catch (error) {
      message.error(UPLOAD_FAILED(file.name));
      onError(error);
    }
  };

  const maxCount = maxFileCount ? maxFileCount : MAX_FILE_COUNT;
  const uploadProps: UploadProps = {
    fileList,
    maxCount: maxCount,
    beforeUpload: (file) => {
      const isDuplicate = fileList.some(
        (existing) => existing.name === file.name
      );

      if (isDuplicate) {
        Modal.confirm({
          title: DUPLICATE_TITLE,
          content: DUPLICATE_WARNING(file.name),
          onOk: () => {
            uploadProps.customRequest?.({
              file,
              onSuccess: () => message.success(UPLOAD_SUCCESS(file.name)),
              onError: (err) => message.error(UPLOAD_FAILED(file.name)),
              onProgress: () => {},
            });
          },
        });
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= maxCount) {
        message.error(MAX_COUNT_WARNING(maxCount));
        return Upload.LIST_IGNORE;
      }

      if (!allowedFileTypes && !file.type.startsWith(FileTypes.IMAGES)) {
        message.error(FILE_TYPE_WARNING);
        return Upload.LIST_IGNORE;
      }

      if (allowedFileTypes && !allowedFileTypes.includes(file.type)) {
        message.error(FILE_TYPE_WARNING_2);
        return Upload.LIST_IGNORE;
      }

      if (file.size > 6 * 1024 * 1024) {
        message.error(FILE_SIZE_WARNING);
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    headers: {
      authorization: "authorization-text",
    },
    customRequest: ({ file, onSuccess, onError, onProgress }) =>
      handleUpload(file, onSuccess, onError, onProgress),
    onChange(info) {
      setFileList(info.fileList);
    },
    onRemove: async (file) => {
      setDocumentKey(undefined);
      setDocumentMeta({});
      setFileList([]);
      setFileAdded?.(false);
      onFilesUploaded?.([]);
      clearReplaceTktError?.();
    },
    listType: "picture",
    progress: { style: { paddingLeft: "0", maxWidth: "98%" },  strokeColor: PROGRESS_COLOR },
  };

  return (
    <div>
      <Label label={DRAGGER_LABEL} />
      <Dragger {...uploadProps} disabled={disabled}>
        <p className={styles.UploadIconContainer}>
          <img src={UploadIcon} alt={UPLOAD_ICON_ALT} />
        </p>
        <p className="ant-upload-text">{UPLOAD_PRIMARY_TXT}</p>
        <p className="ant-upload-hint">{UPLOAD_HINT}</p>
      </Dragger>
    </div>
  );
};

export default AddPhotoDragger;
